import { useCallback, useRef } from "react";
import { getCachedTenantToken } from "../Auth/tenantTokenCache";

export interface iUseTenantTokenProps {
    /** The app-wide selected tenant, whose token is already minted. */
    defaultTenantKey?: string;
    /** `tenantContext.selectedTenant.token`. Used only for `defaultTenantKey`. */
    defaultToken?: string;
}

/**
 * One tenant-scoped token getter, shared by everything on this page.
 *
 * THE DEFAULT TENANT COSTS NO MINT. `selectedTenant.token` was produced by the
 * same `getTenantAuth` call `getCachedTenantToken` would make, so reaching for
 * the cache for that tenant would add an STS round trip for nothing. Every
 * other tenant goes through the cache.
 *
 * Extracted from `useEmailSearch` so the worklist and the email detail share
 * one cache. A second copy would mint again on every drill-down.
 *
 * THE RETURNED FUNCTION IS PERMANENTLY IDENTITY-STABLE, and that is the point
 * of the ref below. `defaultToken` is `selectedTenant.token`, which Main.tsx's
 * `checkTenantToken` REPLACES on a seven-minute timer as expiry nears. Closing
 * over it would put that rotation into the dependency array of every effect
 * that calls this, so a token the request may not even use would re-issue the
 * request — and a spurious re-request that fails turns a healthy page into an
 * error panel with no action of the operator's own. Reading the freshest
 * values at call time is the right semantic anyway: a rotation should be USED
 * BY the next request, not CAUSE one.
 */
export function useTenantToken({ defaultTenantKey, defaultToken }: iUseTenantTokenProps) {
    const tokenInputs = useRef({ defaultTenantKey, defaultToken });
    tokenInputs.current = { defaultTenantKey, defaultToken };

    return useCallback(async (key: string): Promise<string> => {
        const { defaultTenantKey: defaultKey, defaultToken: appToken } = tokenInputs.current;
        if (appToken && key === defaultKey) {
            return appToken;
        }
        return await getCachedTenantToken(key);
    }, []);
}
